import { useSearchParams } from "react-router-dom";

import { MetricDefinitionRecord, Period } from "@/shared/domain/metrics";

import {
  DASHBOARD_PARAMS,
  mergeParams,
  readTiles,
  TileConfig,
  writeTiles,
} from "./dashboardParams";
import { TrendTile } from "./TrendTile";
import styles from "./TrendTileRow.module.css";

export interface TrendTileRowProps {
  definitions: MetricDefinitionRecord[];
  orgId: string | undefined;
  period: Period;
  startTimeNs: number;
  endTimeNs: number;
  deviceIds?: string[];
  sessionIds?: string[];
}

/**
 * Row of KPI tiles. The per-tile metric + aggregation selections are read from
 * (and written back to) the URL so the row restores itself, seeded with the
 * first definitions when the URL carries no tile state.
 */
export function TrendTileRow({
  definitions,
  orgId,
  period,
  startTimeNs,
  endTimeNs,
  deviceIds,
  sessionIds,
}: TrendTileRowProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const allNames = definitions.map((d) => d.name);
  const tiles = readTiles(searchParams, allNames);

  const setTile = (index: number, next: TileConfig) => {
    const updated = tiles.map((tile, i) => (i === index ? next : tile));
    setSearchParams(
      mergeParams(searchParams, {
        [DASHBOARD_PARAMS.tiles]: writeTiles(updated),
      }),
      { replace: true },
    );
  };

  if (tiles.length === 0) {
    return null;
  }

  return (
    <div className={styles.row}>
      {tiles.map((tile, index) => (
        // Tiles are positional slots, so the index is a stable key.
        // eslint-disable-next-line react/no-array-index-key
        <TrendTile
          key={index}
          name={tile.name}
          aggregation={tile.aggregation}
          allNames={allNames}
          onChange={(next) => setTile(index, next)}
          orgId={orgId}
          period={period}
          startTimeNs={startTimeNs}
          endTimeNs={endTimeNs}
          deviceIds={deviceIds}
          sessionIds={sessionIds}
        />
      ))}
    </div>
  );
}
